import React, { useState, FunctionComponent, memo } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { withStyles } from '@material-ui/styles';
import { useHistory } from 'react-router-dom';
import { MedisanteTargetSystem, TargetSystemsSortBy } from '../../types';
import {
  Role,
  hasOneOfGlobalRoles,
  hasOneOfOrgRoles,
} from '../../../../contexts/AuthProvider/types';
import Visible from '../../../../components/Visible/Visible';
import { SortState, SortOption, getSortArrow } from '../../../../utils/sort';
import {
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  FormControlLabel,
  Switch,
  Typography,
  Chip,
  Tooltip,
  Divider,
} from '@material-ui/core';
import { useDesktopLayoutStyles } from '../../styles';
import TargetSystemStatusIcons from '../TargetSystemStatusIcons/TargetSystemStatusIcons';
import { Cached } from '@material-ui/icons';
import { TargetSystemDeviceInfo } from '../../config';
import { getTheme, isDarkTheme } from '../../../../eliot-components/src/theme';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { useSnackbar } from 'notistack';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';

const StyledTableCell = withStyles({
  root: {
    paddingLeft: '10px',
    paddingRight: '10px',
  },
})(TableCell);

interface Props {
  fetchTargetSystems: () => void;
  isLoadingTargetSystems?: boolean;
  targetSystems: MedisanteTargetSystem[];
  sortState: SortState<MedisanteTargetSystem>;
  updateSortState: React.Dispatch<SortOption<TargetSystemsSortBy>>;
  selectedSystem: MedisanteTargetSystem | null;
  setSelectedSystem: React.Dispatch<
    React.SetStateAction<MedisanteTargetSystem | null>
  >;
  removeDevices: React.Dispatch<React.SetStateAction<string | null>>;
  selectedTestSystem: MedisanteTargetSystem | null;
  setSelectedTestSystem: React.Dispatch<
    React.SetStateAction<MedisanteTargetSystem | null>
  >;
  removeTargetSystem: React.Dispatch<
    React.SetStateAction<MedisanteTargetSystem | null>
  >;
  deviceInfo: (tsid: string) => TargetSystemDeviceInfo;
  updateTargetSystem: (ts: MedisanteTargetSystem) => void;
  disableTargetSystem: (ts: MedisanteTargetSystem) => void;
  orgDefaultWebhooks: Record<string, boolean>;
}

interface MenuProps {
  index: Number;
  ts: MedisanteTargetSystem;
  devCount: number;
  setSelectedTestSystem: (ts: MedisanteTargetSystem) => void;
  setSelectedSystem: (ts: MedisanteTargetSystem) => void;
  removeDevices: (devid: string) => void;
  removeTargetSystem: (ts: MedisanteTargetSystem) => void;
  updateTargetSystem: (ts: MedisanteTargetSystem) => void;
  disableTargetSystem: (ts: MedisanteTargetSystem) => void;
}

const MenuHandler: FunctionComponent<MenuProps> = memo(
  ({
    index,
    ts,
    devCount,
    setSelectedTestSystem,
    setSelectedSystem,
    removeDevices,
    removeTargetSystem,
    updateTargetSystem,
    disableTargetSystem,
  }): any => {
    const [anchorEl, setAnchorEl] = useState(null);

    const openMenu = (event: any) => {
      setAnchorEl(event.currentTarget);
    };
    const closeMenu = () => {
      setAnchorEl(null);
    };
    const { authUser } = useAuthUser();
    const hasPerm =
      hasOneOfGlobalRoles(authUser, [Role.DeviceManager]) ||
      hasOneOfOrgRoles(
        authUser,
        ts.owner ? [ts.owner] : authUser.selectedOrgs,
        [Role.DeviceManager]
      );
    const hasGlobalPerm = hasOneOfGlobalRoles(authUser, [Role.DeviceManager]);

    return (
      <>
        <IconButton
          aria-label="more"
          aria-controls={'long-menu-' + index}
          aria-haspopup="true"
          onClick={openMenu}
        >
          <MoreHorizIcon />
        </IconButton>
        <Menu
          id={'long-menu-' + index}
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={closeMenu}
        >
          {ts.isMutable && (
            <MenuItem disabled={!hasPerm}>
              <FormControlLabel
                control={
                  <Switch
                    checked={ts.isDefault}
                    onChange={(e) => {
                      updateTargetSystem({
                        id: ts.id,
                        isDefault: e.target.checked,
                        name: ts.name,
                        url: ts.url,
                      });
                    }}
                    name="isDefault"
                    color="primary"
                  />
                }
                label="Set as default"
              />
            </MenuItem>
          )}

          <MenuItem
            disabled={!hasPerm || ts.deactivated || !ts.isMutable}
            data-testid="test-ts"
            onClick={(e) => {
              closeMenu();
              setSelectedTestSystem(ts);
            }}
          >
            Send Test Measurements{' '}
          </MenuItem>

          {ts.isMutable && (
            <MenuItem
              disabled={!hasPerm}
              data-testid="edit-ts"
              onClick={(e) => {
                closeMenu();
                setSelectedSystem(ts);
              }}
            >
              Edit webhook
            </MenuItem>
          )}

          {devCount > 0 && (
            <MenuItem
              disabled={!hasPerm}
              data-testid="remove-all-devices"
              onClick={(e) => {
                closeMenu();
                removeDevices(ts.id);
              }}
            >
              Remove All devices
            </MenuItem>
          )}

          {devCount === 0 && (
            <MenuItem
              disabled={!hasPerm}
              data-testid="remove-ts"
              onClick={(e) => {
                closeMenu();
                removeTargetSystem(ts);
              }}
            >
              Remove webhook
            </MenuItem>
          )}
          <Divider />
          <MenuItem
            disabled={!hasGlobalPerm}
            data-testid="disabled-ts"
            onClick={() => {
              closeMenu();
              disableTargetSystem(ts);
            }}
          >
            {ts.deactivated && ts.deactivated ? 'Unblock' : 'Block'} webhook
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              let txt = `ID: ${ts.id}
Name: ${ts.name}
URL: ${ts.url}
Format: ${ts.format}
Device count: ${ts.deviceCount || 0}
Owner: ${ts.owner}
Last status code: ${ts.lastStatusCode || 'none'}
Auth type: ${ts.authType}
Is default: ${ts.isDefault ? 'yes' : 'no'}
Request count: ${ts.count || 0}
Test request count: ${ts.testCount || 0}
Deactivated: ${ts.deactivated ? 'yes' : 'no'}\n`;
              navigator.clipboard.writeText(txt);
              closeMenu();
            }}
          >
            Copy to clipboard
          </MenuItem>
        </Menu>
      </>
    );
  }
);

function DesktopLayout(props: Props) {
  const {
    targetSystems,
    sortState,
    updateSortState,
    selectedSystem,
    setSelectedSystem,
    selectedTestSystem,
    setSelectedTestSystem,
    removeDevices,
    removeTargetSystem,
    deviceInfo,
    updateTargetSystem,
    disableTargetSystem,
    orgDefaultWebhooks,
  } = props;
  const classes = useDesktopLayoutStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const setSnack = (snack: string) => {
    if (!!snack) {
      enqueueSnackbar(snack, {
        variant: 'warning',
      });
    }
  };

  const refresh = (
    <div className={classes.refresh}>
      <Visible when={props.isLoadingTargetSystems}>
        <span className={classes.refreshProgressWrapper}>
          <CircularProgress className={classes.refreshProgress} />
        </span>
      </Visible>
      <span className={classes.refreshIcon}>
        <IconButton
          onClick={() => props.fetchTargetSystems()}
          disabled={props.isLoadingTargetSystems}
          title="Refresh webhooks"
        >
          <Cached />
        </IconButton>
      </span>
    </div>
  );

  const viewDevices = (devCount: number, id: string) => {
    if (devCount > 0) {
      history.push(
        `/devices?target_systems=${encodeURIComponent(
          id
        )}&includeStock=true&includeBlocked=true`
      );
    } else {
      setSnack('No devices to show for the target system');
    }
  };

  return (
    <>
      <Paper>
        <div className={classes.container}>
          <Table stickyHeader className={classes.targetSystems}>
            <TableHead>
              <TableRow
                style={{
                  backgroundColor: isDarkTheme(getTheme()) ? '#393' : '#eee',
                }}
              >
                <StyledTableCell style={{ width: '20%' }}>
                  <div
                    className={classes.sortableCell}
                    data-testid="name-header-cell-sort-label"
                    onClick={() =>
                      updateSortState({ sortBy: TargetSystemsSortBy.NAME })
                    }
                  >
                    Name
                    {getSortArrow('name', sortState)}
                  </div>
                </StyledTableCell>
                <StyledTableCell style={{ width: '30%' }}>
                  <div
                    className={classes.sortableCell}
                    data-testid="url-header-cell-sort-label"
                    onClick={() =>
                      updateSortState({ sortBy: TargetSystemsSortBy.URL })
                    }
                  >
                    URL
                    {getSortArrow('url', sortState)}
                  </div>
                </StyledTableCell>
                <StyledTableCell>
                  <div
                    className={`${classes.sortableCell}`}
                    data-testid="status-header-cell-sort-label"
                    onClick={() =>
                      updateSortState({
                        sortBy: TargetSystemsSortBy.LASTSTATUSCODE,
                      })
                    }
                  >
                    Status
                    {getSortArrow('lastStatusCode', sortState)}
                  </div>
                </StyledTableCell>
                <StyledTableCell>
                  <div
                    className={`${classes.sortableCell} ${classes.sortableCenter}`}
                    data-testid="devices-header-cell-sort-label"
                    onClick={() =>
                      updateSortState({
                        sortBy: TargetSystemsSortBy.DEVICECOUNT,
                      })
                    }
                  >
                    Devices
                    {getSortArrow('deviceCount', sortState)}
                  </div>
                </StyledTableCell>
                <StyledTableCell>
                  <div
                    className={`${classes.sortableCell} ${classes.sortableCenter}`}
                    data-testid="requests-header-cell-sort-label"
                    onClick={() =>
                      updateSortState({
                        sortBy: TargetSystemsSortBy.REQUESTS,
                      })
                    }
                  >
                    Requests
                    {getSortArrow('count', sortState)}
                  </div>
                </StyledTableCell>
                <StyledTableCell>
                  <div
                    className={`${classes.sortableCell} ${classes.sortableCenter}`}
                    data-testid="requests-test-header-cell-sort-label"
                    onClick={() =>
                      updateSortState({
                        sortBy: TargetSystemsSortBy.REQUESTS_TEST,
                      })
                    }
                  >
                    Test Requests
                    {getSortArrow('testCount', sortState)}
                  </div>
                </StyledTableCell>
                <StyledTableCell>{refresh}</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {targetSystems.map((ts, index) => {
                const info = deviceInfo(ts.id);
                const devCount = info ? info.count : 0;
                const ownerUsesOrgDefault = !!(
                  ts.isDefault &&
                  ts.owner &&
                  orgDefaultWebhooks[ts.owner]
                );
                return (
                  <TableRow
                    hover
                    key={index + '-' + ts.name + '-' + ts.id}
                    data-id={ts.id}
                    className={`${
                      selectedSystem === ts || selectedTestSystem === ts
                        ? classes.selected
                        : classes.tsRow
                    } ${ts.isDefault ? classes.defaultTS : ''}`}
                  >
                    <StyledTableCell data-testid="name">
                      <div className={classes.breakWord}>{ts.name}</div>
                    </StyledTableCell>
                    <StyledTableCell data-testid="url">
                      <div className={classes.breakWord}>{ts.url}</div>
                    </StyledTableCell>
                    <StyledTableCell>
                      <TargetSystemStatusIcons
                        targetSystem={ts}
                        incompatibile={info ? info.incompatibile : false}
                      />
                    </StyledTableCell>
                    <StyledTableCell data-testid="devices">
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          if (!ownerUsesOrgDefault) {
                            viewDevices(devCount, ts.id);
                          }
                        }}
                      >
                        <Typography
                          align="center"
                          variant="inherit"
                          display="block"
                        >
                          {ownerUsesOrgDefault
                            ? '-'
                            : devCount.toLocaleString()}
                        </Typography>
                      </div>
                    </StyledTableCell>

                    <StyledTableCell data-testid="requests" align="center">
                      <Tooltip title="Real requests" aria-label="real requests">
                        <Chip
                          label={ts.count ? ts.count.toLocaleString() : 0}
                          color="secondary"
                          className={classes.countBadge}
                        />
                      </Tooltip>
                    </StyledTableCell>

                    <StyledTableCell
                      data-testid="requests-test-test"
                      align="center"
                    >
                      <Tooltip title="Test requests" aria-label="test requests">
                        <Chip
                          label={
                            ts.testCount ? ts.testCount.toLocaleString() : 0
                          }
                          className={classes.testCountBadge}
                        />
                      </Tooltip>
                    </StyledTableCell>

                    <StyledTableCell align="right">
                      <MenuHandler
                        index={index}
                        ts={ts}
                        devCount={devCount}
                        removeDevices={removeDevices}
                        removeTargetSystem={removeTargetSystem}
                        setSelectedSystem={setSelectedSystem}
                        setSelectedTestSystem={setSelectedTestSystem}
                        updateTargetSystem={updateTargetSystem}
                        disableTargetSystem={disableTargetSystem}
                      ></MenuHandler>
                    </StyledTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <Grid
          container
          className={classes.summary}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Grid item className={classes.summaryItem}>
            Total webhooks count: {targetSystems.length}
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}

export default DesktopLayout;
